import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Store } from '@ngrx/store';
import { Observable, of, throwError } from 'rxjs';
import { catchError, take } from 'rxjs/operators';

import { ConfigService, SentryLogger } from '@fcom/core';
import { DapiHttpResponse } from '@fcom/dapi/api/dapi-http-response';
import {
  FinnairAlternativeOffer,
  FinnairAlternativeOffers,
  FinnairSelfReacConfirmationRequest,
  FinnairSelfReacConfirmationResponse,
} from '@fcom/dapi/api/models';
import { OffersSelfReaccService, SelfReaccService } from '@fcom/dapi/api/services';

import { AlternativeOffersStateStatus, ReaccommodationFeatureState } from '../interfaces';
import { AlternativeOffersActions } from '../store';

export enum AlternativeOffersHttpError {
  failedToRetrieve = 'Failed to retrieve alternative offers, reverting to error page',
  failedToRetrieveDatePicker = 'Failed to retrieve alternative offers from datepicker',
  failedToRetrieveDeeplink = 'Failed to retrieve alternative offers from deeplink',
  failedToConfirm = 'Failed to confirm alternative offers or to acknowledge',
}

@Injectable()
export class AlternativeOffersService {
  constructor(
    private store$: Store<ReaccommodationFeatureState>,
    private selfReaccService: SelfReaccService,
    private offerSelfReaccService: OffersSelfReaccService,
    private configService: ConfigService,
    private sentryLogger: SentryLogger
  ) {}

  getAlternativeOffers(
    params: {
      orderId: string;
      boundIndex: number;
      hash: string;
      locale: string;
      startDate: string;
    },
    showLoading = true,
    errorString = AlternativeOffersHttpError.failedToRetrieve
  ): Observable<{ [key: string]: FinnairAlternativeOffers } | HttpErrorResponse> {
    if (showLoading) {
      this.store$.dispatch(AlternativeOffersActions.setStatus({ status: AlternativeOffersStateStatus.PENDING }));
    }

    const { hash, locale, orderId, boundIndex } = params;

    return this.offerSelfReaccService
      .disruptionAlternativeOffers(this.configService.cfg.offersUrl, {
        hash,
        locale,
        orderId,
        boundSearchDate: boundIndex === 0 ? params.startDate : undefined,
        secondBoundSearchDate: boundIndex === 1 ? params.startDate : undefined,
      })
      .pipe(
        // eslint-disable-next-line rxjs/no-implicit-any-catch
        catchError((err: HttpErrorResponse) => this.handleError(err, errorString))
      );
  }

  confirmAcknowledge(params: {
    boundIndexes: number[];
    orderId: string;
    hash: string;
    locale: string;
  }): Observable<HttpErrorResponse | FinnairSelfReacConfirmationResponse> {
    return this.selfReaccService
      .disruptionConfirmAcknowledge(this.configService.cfg.orderUrl, {
        ...params,
        body: {
          boundIndexes: params.boundIndexes,
        },
      })
      .pipe(
        take(1),
        // eslint-disable-next-line rxjs/no-implicit-any-catch
        catchError((err: HttpErrorResponse) => this.handleError(err, AlternativeOffersHttpError.failedToConfirm))
      );
  }

  confirmAlternatives(params: {
    offerIdByBoundIndex: Record<string, string>;
    orderId: string;
    hash: string;
    locale: string;
  }): Observable<DapiHttpResponse<FinnairSelfReacConfirmationResponse>> {
    return this.selfReaccService
      .disruptionConfirmAlternative$Response(this.configService.cfg.orderUrl, {
        ...params,
        body: {
          offerIdByBoundIndex: params.offerIdByBoundIndex,
        },
      })
      .pipe(
        take(1),
        // eslint-disable-next-line rxjs/no-implicit-any-catch
        catchError((err: HttpErrorResponse) => throwError(() => err))
      );
  }

  confirmSelectedOffers(params: {
    disruptedBoundIndex: number;
    offer: FinnairAlternativeOffer;
    orderId: string;
    hash: string;
    locale: string;
  }): Observable<HttpErrorResponse | FinnairSelfReacConfirmationResponse> {
    const disruptionConfirmationRequest: FinnairSelfReacConfirmationRequest = {
      data: {
        boundIndex: params.disruptedBoundIndex,
        offerId: params.offer.id,
      },
    };
    const { hash, orderId, locale } = params;
    return this.selfReaccService
      .disruptionConfirmAlternative(this.configService.cfg.orderUrl, {
        hash,
        orderId,
        locale,
        body: disruptionConfirmationRequest,
      })
      .pipe(
        take(1),
        // eslint-disable-next-line rxjs/no-implicit-any-catch
        catchError((err: HttpErrorResponse) => this.handleError(err, AlternativeOffersHttpError.failedToConfirm))
      );
  }

  private handleError(error: HttpErrorResponse, errorString: AlternativeOffersHttpError) {
    this.sentryLogger.error(errorString, { error });
    this.store$.dispatch(AlternativeOffersActions.setStatus({ status: AlternativeOffersStateStatus.ERROR }));
    return of(error);
  }
}
